import { useEffect, useRef } from "react";

export default function MatrixRain({ opacity = 0.4, speed = 1 }) {
  const canvasRef = useRef(null);
  const streamsRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    // More authentic Matrix character set - heavy on katakana and half-width katakana
    const katakana = "アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン";
    const halfKatakana = "ｦｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉﾊﾋﾌﾍﾎﾏﾐﾑﾒﾓﾔﾕﾖﾗﾘﾙﾚﾛﾜﾝ";
    const symbols = "0123456789:・.=*+-<>¦|_";
    const chars = katakana + halfKatakana + symbols;

    let frame;
    let last = performance.now();

    const fontSize = 15;
    const columnWidth = fontSize * 0.85;

    // Stream object: { y, speed, length, chars[], brightIndex }
    const createStream = (x, startY = null) => {
      const streamLength = Math.floor(Math.random() * 15) + 8; // 8-22 characters
      const streamSpeed = (Math.random() * 0.5 + 0.5) * speed; // Variable speed
      const streamChars = [];
      for (let i = 0; i < streamLength; i++) {
        streamChars.push(chars.charAt(Math.floor(Math.random() * chars.length)));
      }
      return {
        x,
        y: startY !== null ? startY : -Math.random() * canvas.height * 0.5,
        speed: streamSpeed,
        length: streamLength,
        chars: streamChars,
        changeTimer: 0,
      };
    };

    const setup = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      const columns = Math.floor(canvas.width / columnWidth);

      // Initialize streams - some columns may have multiple streams
      if (!streamsRef.current.length || Math.abs(streamsRef.current.length - columns) > 5) {
        streamsRef.current = [];
        for (let i = 0; i < columns; i++) {
          // Random initial positions for varied look
          streamsRef.current.push(createStream(i * columnWidth, -Math.random() * canvas.height));
        }
      }
    };
    setup();

    const draw = (now) => {
      const dt = Math.max(0.1, (now - last) / 16.67);
      last = now;

      // Fade effect - darker = longer trails
      ctx.fillStyle = `rgba(0, 8, 2, ${opacity * 0.85})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.font = `${fontSize}px 'MS Gothic', 'Hiragino Kaku Gothic Pro', monospace`;
      ctx.textBaseline = "top";

      const streams = streamsRef.current;

      for (let i = 0; i < streams.length; i++) {
        const stream = streams[i];

        // Randomly change characters in the stream (Matrix effect)
        stream.changeTimer += dt;
        if (stream.changeTimer > 3) {
          stream.changeTimer = 0;
          const changeIndex = Math.floor(Math.random() * stream.chars.length);
          stream.chars[changeIndex] = chars.charAt(Math.floor(Math.random() * chars.length));
        }

        // Draw each character in the stream with gradient brightness
        for (let j = 0; j < stream.length; j++) {
          const charY = stream.y - j * fontSize;

          // Skip if off screen
          if (charY < -fontSize || charY > canvas.height + fontSize) continue;

          // Calculate brightness: head is brightest, fades down the trail
          let brightness;
          if (j === 0) {
            // Head character - white/very bright
            brightness = 1;
            ctx.fillStyle = "#ffffff";
            ctx.shadowColor = "#68ff9a";
            ctx.shadowBlur = 8;
          } else if (j < 3) {
            // Near head - bright green
            brightness = 0.9 - j * 0.1;
            ctx.fillStyle = `rgba(104, 255, 154, ${brightness})`;
            ctx.shadowBlur = 4;
          } else {
            // Trail - fading green
            brightness = Math.max(0.15, 0.7 - (j / stream.length) * 0.6);
            const green = Math.floor(180 + brightness * 75);
            ctx.fillStyle = `rgba(30, ${green}, 80, ${brightness})`;
            ctx.shadowBlur = 0;
          }

          ctx.fillText(stream.chars[j], stream.x, charY);
        }

        // Reset shadow
        ctx.shadowBlur = 0;

        // Move stream down
        stream.y += stream.speed * dt * fontSize * 0.15;

        // Reset stream when it goes off screen
        const streamBottom = stream.y - stream.length * fontSize;
        if (streamBottom > canvas.height) {
          // Reset with new properties
          Object.assign(stream, createStream(stream.x, -Math.random() * fontSize * 10));
        }
      }

      frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);

    const handleResize = () => {
      setup();
    };
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", handleResize);
    };
  }, [opacity, speed]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 0,
        width: "100vw",
        height: "100vh",
        pointerEvents: "none",
      }}
    />
  );
}
